// Elude home sections: hero film, complete-the-fit hotspots + add all, best-seller tabs/carousel,
// shoppable looks carousel, parallax banner, reviews carousel. Idempotent; re-inits on theme-editor section load.
(() => {
  if (window.EludeHome) return;
  const mqMobile = window.matchMedia('(max-width: 1023px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const once = (el, key) => { if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; };

  /* Hero film: pick landscape/portrait source by breakpoint, autoplay muted unless reduced motion. */
  function hero(root) {
    if (!once(root, 'eInit')) return;
    const v = root.querySelector('.e-hero__video');
    const btn = root.querySelector('[data-e-hero-toggle]');
    if (!v) return;
    let userPaused = reduce.matches;
    const setState = (paused) => {
      root.classList.toggle('is-paused', paused);
      if (btn) btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
    };
    const pick = () => {
      const src = mqMobile.matches ? v.dataset.eSrcMobile : v.dataset.eSrcDesktop;
      if (mqMobile.matches && v.dataset.ePosterMobile) v.poster = v.dataset.ePosterMobile;
      if (src && v.getAttribute('src') !== src) { v.src = src; v.load(); }
    };
    const play = () => { pick(); const p = v.play(); if (p && p.catch) p.catch(() => setState(true)); };
    v.muted = true;
    v.addEventListener('playing', () => { v.classList.add('is-ready'); setState(false); });
    v.addEventListener('pause', () => setState(true));
    setState(userPaused);
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { if (!userPaused) play(); else pick(); } else if (!v.paused) v.pause();
    }), { rootMargin: '100px 0px' });
    io.observe(root);
    mqMobile.addEventListener('change', () => { pick(); if (!userPaused) play(); });
    if (btn) btn.addEventListener('click', () => {
      if (v.paused) { userPaused = false; play(); } else { userPaused = true; v.pause(); }
    });
  }

  /* Complete the fit: dots select a product (row highlight) and open its card; Add all = one /cart/add.js call. */
  function fit(root) {
    if (!once(root, 'eInit')) return;
    const spots = [...root.querySelectorAll('[data-e-fit-spot]')];
    const rows = [...root.querySelectorAll('[data-e-fit-row]')];
    const cards = [...root.querySelectorAll('[data-e-fit-card]')];
    const media = root.querySelector('.e-fit__media');
    let open = -1;
    const close = (focus) => {
      if (open < 0) return;
      const i = open; open = -1;
      cards[i] && (cards[i].hidden = true);
      spots[i] && spots[i].setAttribute('aria-expanded', 'false');
      if (focus && spots[i]) spots[i].focus();
    };
    const place = (card, spot) => {
      const mw = media.clientWidth, mh = media.clientHeight;
      const x = spot.offsetLeft, y = spot.offsetTop;
      card.hidden = false;
      const cw = card.offsetWidth, ch = card.offsetHeight;
      const left = Math.max(12, Math.min(mw - cw - 12, x - cw / 2));
      let top = y + 30;
      if (top + ch > mh - 12) top = y - 30 - ch;
      card.style.left = left + 'px'; card.style.top = Math.max(12, top) + 'px';
    };
    const select = (i) => {
      spots.forEach((s, k) => s.classList.toggle('is-on', k === i));
      rows.forEach((r, k) => r.classList.toggle('is-on', k === i));
    };
    spots.forEach((s, i) => s.addEventListener('click', (e) => {
      e.stopPropagation();
      const wasOpen = open === i;
      close(false);
      select(i);
      if (wasOpen) return;
      open = i; s.setAttribute('aria-expanded', 'true');
      if (cards[i]) place(cards[i], s);
    }));
    document.addEventListener('click', (e) => { if (open >= 0 && !cards[open].contains(e.target)) close(false); });
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open >= 0) close(true); });
    const add = root.querySelector('[data-e-fit-add]');
    const err = root.querySelector('.e-fit__err');
    if (add) add.addEventListener('click', async () => {
      const items = add.dataset.eFitAdd.split(',').filter(Boolean).map((id) => ({ id: Number(id), quantity: 1 }));
      if (!items.length || !window.EludeCart) return;
      add.setAttribute('aria-busy', 'true'); if (err) err.textContent = '';
      try { await window.EludeCart.add(items, add); }
      catch (x) { if (err) err.textContent = x.message; }
      finally { add.removeAttribute('aria-busy'); }
    });
  }

  /* Best sellers: tabs (click + arrow keys) and optional carousel arrows. */
  function bestSellers(root) {
    if (!once(root, 'eInit')) return;
    const tabs = [...root.querySelectorAll('[data-e-bs-tab]')];
    const panels = [...root.querySelectorAll('[data-e-bs-panel]')];
    const prev = root.querySelector('[data-e-bs-prev]'), next = root.querySelector('[data-e-bs-next]');
    const track = () => panels.find((p) => !p.hidden)?.querySelector('[data-e-bs-track]');
    const sync = () => {
      const t = track(); if (!t || !prev) return;
      prev.disabled = t.scrollLeft <= 2;
      next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
    };
    const show = (i, focus) => {
      tabs.forEach((t, k) => { const on = k === i; t.classList.toggle('is-on', on); t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; });
      const lk = root.querySelector('[data-e-bs-link]'); if (lk && tabs[i] && tabs[i].dataset.eBsUrl) lk.href = tabs[i].dataset.eBsUrl;
      panels.forEach((p, k) => { p.hidden = k !== i; });
      if (focus) tabs[i].focus();
      const t = track(); if (t) t.scrollLeft = 0; sync();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => show(i));
      t.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (e.key === 'Home') { e.preventDefault(); show(0, true); }
        else if (e.key === 'End') { e.preventDefault(); show(tabs.length - 1, true); }
        else if (d) { e.preventDefault(); show((i + d + tabs.length) % tabs.length, true); }
      });
    });
    if (prev) {
      const step = (dir) => { const t = track(); if (!t) return; const c = t.querySelector('.e-bs__cell'); t.scrollBy({ left: dir * ((c ? c.offsetWidth : 300) + 8), behavior: reduce.matches ? 'auto' : 'smooth' }); };
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      panels.forEach((p) => p.querySelector('[data-e-bs-track]')?.addEventListener('scroll', sync, { passive: true }));
      sync();
    }
  }

  /* Shoppable looks: desktop rotates the active look into the centre (CSS order); mobile is a native swipe row. */
  function seen(root) {
    if (!once(root, 'eInit')) return;
    const track = root.querySelector('[data-e-seen-track]');
    const items = [...root.querySelectorAll('[data-e-seen-item]')];
    const n = items.length; if (!n) return;
    const half = Math.floor(n / 2);
    let cur = Number(root.dataset.start) || 0;
    const render = (dir) => {
      items.forEach((it, i) => {
        it.style.setProperty('--o', (i - cur + n + half) % n);
        const on = i === cur;
        it.classList.toggle('is-on', on);
        const hit = it.querySelector('.e-seen__hit'); if (hit) hit.tabIndex = on ? -1 : 0;
      });
      if (dir && !reduce.matches && !mqMobile.matches) {
        track.classList.remove('is-l', 'is-r'); void track.offsetWidth;
        track.classList.add(dir > 0 ? 'is-l' : 'is-r');
      }
    };
    const go = (i, dir) => { cur = (i + n) % n; render(dir); };
    root.querySelector('[data-e-seen-next]')?.addEventListener('click', () => go(cur + 1, 1));
    root.querySelector('[data-e-seen-prev]')?.addEventListener('click', () => go(cur - 1, -1));
    items.forEach((it, i) => it.querySelector('[data-e-seen-pick]')?.addEventListener('click', () => {
      const d = ((i - cur + n) % n) <= half ? 1 : -1; go(i, d);
      it.querySelector('.e-seen__add, .e-seen__txt')?.focus({ preventScroll: true });
    }));
    track.addEventListener('animationend', () => track.classList.remove('is-l', 'is-r'));
  }

  /* Parallax: image slower than the page, floating card drifts. Off under reduced motion. */
  const plxRoots = new Set();
  let plxRaf = 0;
  const plxApply = () => {
    plxRaf = 0;
    const vh = window.innerHeight;
    plxRoots.forEach((root) => {
      const band = root.querySelector('.e-plx__band') || root;
      const r = band.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      const p = Math.max(0, Math.min(1, (vh - r.top) / (vh + r.height)));
      root.querySelectorAll('[data-e-plx-img]').forEach((img) => { img.style.transform = 'translateY(' + (-Number(img.dataset.ePlxImg) * p).toFixed(2) + '%)'; });
      const card = root.querySelector('[data-e-plx-float]');
      if (card) card.style.transform = 'translateY(' + (140 - 280 * p).toFixed(1) + 'px)';
    });
  };
  const plxSched = () => { if (!plxRaf && !reduce.matches) plxRaf = requestAnimationFrame(plxApply); };
  function parallax(root) {
    if (!once(root, 'eInit')) return;
    plxRoots.add(root);
    plxSched();
  }
  window.addEventListener('scroll', plxSched, { passive: true });
  window.addEventListener('resize', plxSched);
  reduce.addEventListener('change', () => {
    if (reduce.matches) plxRoots.forEach((r) => r.querySelectorAll('[data-e-plx-img], [data-e-plx-float]').forEach((el) => { el.style.transform = ''; }));
    else plxSched();
  });

  /* Reviews: desktop arrows slide the row by one card. */
  function reviews(root) {
    if (!once(root, 'eInit')) return;
    const vp = root.querySelector('[data-e-rev-vp]'), track = root.querySelector('[data-e-rev-track]');
    const prev = root.querySelector('[data-e-rev-prev]'), next = root.querySelector('[data-e-rev-next]');
    if (!vp || !track || !prev) return;
    let x = 0;
    const max = () => Math.max(0, track.scrollWidth - (vp.clientWidth - 24) + 0);
    const stepW = () => { const c = track.firstElementChild; return c ? c.offsetWidth + 8 : 388; };
    const apply = () => {
      if (mqMobile.matches) { track.style.transform = ''; return; }
      x = Math.max(0, Math.min(x, max()));
      track.style.transform = x ? 'translateX(-' + x + 'px)' : '';
      prev.disabled = x <= 0; next.disabled = x >= max() - 1;
    };
    prev.addEventListener('click', () => { x = Math.max(0, x - stepW()); apply(); });
    next.addEventListener('click', () => { x = Math.min(max(), x + stepW()); apply(); });
    window.addEventListener('resize', apply);
    apply();
  }

  const MAP = { '[data-e-hero]': hero, '[data-e-fit]': fit, '[data-e-bs]': bestSellers, '[data-e-seen]': seen, '[data-e-plx]': parallax, '[data-e-rev]': reviews };
  const init = (scope = document) => Object.entries(MAP).forEach(([sel, fn]) => scope.querySelectorAll(sel).forEach((el) => fn(el)));
  window.EludeHome = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Theme editor: selecting a block in the sidebar shows it — switches to that tab, or brings that look to the front.
  document.addEventListener('shopify:block:select', (e) => {
    const t = e.target;
    if (t.matches && t.matches('[data-e-bs-tab]')) t.click();
    const pick = t.closest && t.closest('[data-e-seen-item]');
    if (pick) { const hit = pick.querySelector('[data-e-seen-pick]'); if (hit) hit.click(); }
  });
})();
